import { useState } from 'react';
import { CheckboxFilter } from '../components/CheckboxFilter';
import { CompanyCard } from '../components/CompanyCard';
import { PageHeader } from '../components/PageHeader';
import { ResultSummary } from '../components/ResultSummary';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel';
import { TruncationNotice } from '../components/TruncationNotice';
import { useDebounced } from '../hooks/useDebounced';
import { useCompanies, useTaxonomy } from '../hooks/useRadarQuery';

export function CompaniesPage() {
  const [q, setQ] = useState('');
  // Sets rather than single values, as the jobs view already holds its role
  // families: "chemistry or molecular biology" is one question a reader asks
  // rather than two passes to merge by eye. A company also carries several
  // domains at once, so a single-valued filter could not express a question the
  // records themselves answer. Empty means the filter asks nothing.
  const [domain, setDomain] = useState<string[]>([]);
  const [stage, setStage] = useState<string[]>([]);
  const [reviewStatus, setReviewStatus] = useState<string[]>([]);
  // The filters are sent to the API rather than applied to what it returned.
  // Filtering a fetched page reported the matches within that page as the size
  // of the match, so a registry past the cap answered a narrowing question with
  // a number about an arbitrary slice of itself.
  const companies = useCompanies({ q: useDebounced(q), domain, stage, reviewStatus });
  const taxonomy = useTaxonomy();

  if (companies.isLoading || taxonomy.isLoading) return <LoadingPanel />;
  if (companies.isError || taxonomy.isError || !taxonomy.data) return <ErrorPanel error={companies.error ?? taxonomy.error} />;
  const matched = companies.data?.total ?? 0;
  const shown = companies.data?.items ?? [];
  return (
    <>
      <PageHeader eyebrow="Landscape" title="Company registry" description="Verified companies, pending candidates, and strategic comparables remain distinguishable rather than being flattened into a single market map." />
      <section className="filter-bar">
        <label className="search-field"><span>Search</span><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Company or technical thesis" /></label>
        <CheckboxFilter legend="Domain" emptyLabel="All domains" options={taxonomy.data.domains} selected={domain} onChange={setDomain} />
        <CheckboxFilter legend="Stage" emptyLabel="All stages" options={taxonomy.data.fundingStages} selected={stage} onChange={setStage} />
        <CheckboxFilter legend="Review" emptyLabel="All states" options={taxonomy.data.reviewStatuses} selected={reviewStatus} onChange={setReviewStatus} />
      </section>
      <ResultSummary
        count={matched}
        one="company matches the current filters."
        many="companies match the current filters."
      />
      <TruncationNotice shown={shown.length} total={matched} noun="companies" />
      <section className="card-grid">{shown.length ? shown.map((company) => <CompanyCard key={company.id} company={company} />) : <EmptyPanel message="No companies match these filters." />}</section>
    </>
  );
}
